import { useEffect, useState } from "react";
import { get, ref } from "firebase/database";
import { addMonths } from "date-fns";
import { db } from "../lib/firebase";
import { getPathDatabaseByRole } from "../actions/auth";
import { useDispatchApp, useSelectorApp } from "../lib/redux";

const MINIMUM_TERM_CONTRACT_MONTHS = 3;

export const isMinimumTermContractCancellationBlocked = ({
  hasMinimumTermContractClause,
  creationTime,
  now = Date.now(),
} = {}) => {
  if (hasMinimumTermContractClause !== true) {
    return false;
  }

  const parsedCreationTime = Number(creationTime);
  if (!Number.isFinite(parsedCreationTime)) {
    return false;
  }

  return (
    addMonths(
      new Date(parsedCreationTime),
      MINIMUM_TERM_CONTRACT_MONTHS
    ).getTime() > now
  );
};

/**
 * Reads the agency permanence clause and agencies/{agencyID}/creationTime.
 * Unresolved means cancel stays hidden. A failed read or missing creationTime
 * leaves cancellation available.
 */
const useMinimumTermContractClause = () => {
  const dispatch = useDispatchApp();
  const authUser = useSelectorApp((state) => state.auth.user);
  const [isCancellationBlocked, setIsCancellationBlocked] = useState(false);
  const [isResolved, setIsResolved] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const { agencyID } = dispatch(getPathDatabaseByRole());

      if (!agencyID) {
        if (!cancelled) {
          setIsCancellationBlocked(false);
          setIsResolved(Boolean(authUser?.uid));
        }
        return;
      }

      try {
        const [clauseSnapshot, creationTimeSnapshot] = await Promise.all([
          get(ref(db, `agencies/${agencyID}/hasMinimumTermContractClause`)),
          get(ref(db, `agencies/${agencyID}/creationTime`)),
        ]);

        if (!cancelled) {
          setIsCancellationBlocked(
            isMinimumTermContractCancellationBlocked({
              hasMinimumTermContractClause: clauseSnapshot.val(),
              creationTime: creationTimeSnapshot.val(),
            })
          );
          setIsResolved(true);
        }
      } catch {
        if (!cancelled) {
          setIsCancellationBlocked(false);
          setIsResolved(true);
        }
      }
    };

    if (!authUser?.uid) {
      setIsCancellationBlocked(false);
      setIsResolved(false);
      return () => {
        cancelled = true;
      };
    }

    setIsResolved(false);
    run();

    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line
  }, [authUser?.uid, authUser?.agencyID]);

  return { isCancellationBlocked, isResolved };
};

export default useMinimumTermContractClause;
